/* THE HELP CENTRE ON juce.co.za.
 *
 * The words live on jennie.juce.co.za; this draws them inside the shop. The
 * whole feature is one Shopify page - /pages/help - and this module is the
 * router, the renderer and the search box for it.
 *
 * WHY A SEPARATE MODULE AND NOT theme.js. theme.js is twelve thousand lines
 * loaded on every page of the store, and this is needed on exactly one URL.
 * Mounting it from the snippet keeps the help centre off the critical path of
 * every product page, and keeps a file several sessions edit at once out of
 * the blast radius of a help-centre change. Same trade as rma.js.
 *
 * WHY THE ADDRESSES ARE QUERY PARAMETERS. A Shopify page has no sub-paths:
 * /pages/help/returns is a 404 the theme never sees. So an article is
 * /pages/help?a=<slug> and a category is /pages/help?c=<slug>, pushed with the
 * History API so back and forward behave and a link can be copied out of the
 * bar. It is the one real compromise in putting this inside the storefront,
 * and it is why the SEO-authoritative copy of the same content is the
 * server-rendered one at jennie.juce.co.za/help, which has a real URL per
 * article. This page says so: every render sets <link rel=canonical> to the
 * address the API hands back, so the two surfaces never compete.
 *
 * WHAT IT DOES FOR SEARCH ENGINES. Title, meta description, canonical and
 * JSON-LD are all set per view. A crawler that runs JavaScript sees a complete
 * head; one that does not sees the noscript block in the snippet, which links
 * straight to the server-rendered help centre. Neither is stranded.
 *
 * NOTHING HERE TRUSTS THE API'S TEXT AS MARKUP except one field: `html`, which
 * is the rendered markdown the server produced from its own tiny renderer
 * (core/help.ts, no raw HTML, no images, no tables). Every other string - a
 * title, a summary, a category name - is written with textContent.
 */

const MOUNT = '[data-help]';

/* ------------------------------------------------------------ helpers ---- */

const el = (tag, cls, text) => {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
};

const ICONS = {
  reward: 'M12 3l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.6 9.1l5.8-.8z',
  account: 'M12 4.4a3.6 3.6 0 1 1 0 7.2 3.6 3.6 0 0 1 0-7.2M4.5 20a7.5 7.5 0 0 1 15 0',
  product: 'M9 3h6v3H9zM7.5 6h9l1.2 13.2a1.5 1.5 0 0 1-1.5 1.8H7.8a1.5 1.5 0 0 1-1.5-1.8z',
  delivery: 'M3 7h11v9H3zM14 10h4l3 3v3h-7M5.2 18a1.8 1.8 0 1 1 3.6 0 1.8 1.8 0 0 1-3.6 0M15.2 18a1.8 1.8 0 1 1 3.6 0 1.8 1.8 0 0 1-3.6 0',
  payment: 'M5.5 6h13a2.5 2.5 0 0 1 2.5 2.5v7a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 15.5v-7A2.5 2.5 0 0 1 5.5 6M3 10.5h18',
  return: 'M4 9h11a5 5 0 0 1 0 10H8M8 5L4 9l4 4',
  privacy: 'M12 3l7 3v6c0 4.4-3 7.9-7 9-4-1.1-7-4.6-7-9V6zM9.5 12.2l1.8 1.8 3.4-3.6',
  book: 'M5 4.5h9a3 3 0 0 1 3 3V20a2.5 2.5 0 0 0-2.5-2.5H5zM17 7.5h2v12h-2',
};

function icon(name) {
  const wrap = el('span', 'hlp__ico');
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', ICONS[name] || ICONS.book);
  svg.appendChild(path);
  wrap.appendChild(svg);
  return wrap;
}

/* One place that decides what a link on this page points at, so the router and
   the markup can never disagree about the shape of an address. */
const hrefFor = (kind, slug) =>
  kind === 'home' ? '/pages/help' : `/pages/help?${kind === 'category' ? 'c' : 'a'}=${encodeURIComponent(slug)}`;

/* --------------------------------------------------------------- head ---- */

function meta(name, value, attr) {
  const key = attr || 'name';
  let tag = document.head.querySelector(`meta[${key}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(key, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
}

/* THE CANONICAL IS NOT THIS PAGE, and that is deliberate.

   The same answers are served from two places: here, drawn by script under one
   Shopify URL, and jennie.juce.co.za/help, server-rendered with one real URL
   per article. `url` from the API is whichever of those the shop has named as
   canonical (the help.canonical_base dial), so pointing at it is what stops
   the two surfaces competing in a search result. Shopify's own canonical tag
   is replaced rather than added to - two of them is the same as none. */
function head({ title, description, canonical, jsonLd }) {
  document.title = title;
  meta('description', description);
  meta('og:title', title, 'property');
  meta('og:description', description, 'property');
  if (canonical) {
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement('link'); link.rel = 'canonical'; document.head.appendChild(link); }
    link.href = canonical;
    meta('og:url', canonical, 'property');
  }
  document.querySelectorAll('script[data-help-ld]').forEach((n) => n.remove());
  (jsonLd || []).forEach((obj) => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute('data-help-ld', '1');
    s.textContent = JSON.stringify(obj);
    document.head.appendChild(s);
  });
}

/* --------------------------------------------------------------- init ---- */

let mounted = false;

export default function initHelp() {
  const root = document.querySelector(MOUNT);
  /* A REAL GUARD, not a hopeful comment. This module self-starts on import and
     also exports its entry point, so a second call - a future theme.js that
     learns about it, a hot reload - would otherwise stack a second set of
     click, popstate and submit listeners on the same nodes and fire every
     search twice. */
  if (!root || mounted) return;
  mounted = true;

  const api = (root.dataset.helpApi || '').replace(/\/+$/, '');
  const view = root.querySelector('[data-help-view]');
  const findForm = root.querySelector('[data-help-find]');
  const findInput = findForm && findForm.querySelector('input[name=q]');
  const sug = root.querySelector('[data-help-sug]');
  const hero = root.querySelector('[data-help-hero]');
  const shopName = root.dataset.helpShop || 'JUCE';
  if (!api || !view) return;

  /* One in-flight index for the whole session. The categories and their
     articles are one request and they do not change while somebody reads. */
  let index = null;
  let indexFlight = null;
  const articles = new Map();
  let lastQueryId = null;

  const get = (path) =>
    fetch(api + path, { credentials: 'omit' }).then((r) => {
      if (!r.ok) throw new Error(String(r.status));
      return r.json();
    });

  const loadIndex = () => {
    if (index) return Promise.resolve(index);
    if (!indexFlight) {
      indexFlight = get('/api/help/index').then((d) => { index = d; return d; });
      indexFlight.catch(() => { indexFlight = null; });
    }
    return indexFlight;
  };

  /* ------------------------------------------------------------ paint ---- */

  /* THE PAGE HAS ONE h1 AND ONE BREADCRUMB AT A TIME. The snippet ships the
     index's crumb and heading in the markup so the page is not blank before
     the first fetch lands; every other view renders its own. Leaving both
     standing gave a category page two breadcrumbs and an article an <h1> above
     its own title, which is a broken document outline on the surface a search
     engine is meant to read. The search form is not inside that wrapper and
     never hides. */
  const setHero = (show) => { if (hero) hero.hidden = !show; };

  const clear = () => { while (view.firstChild) view.removeChild(view.firstChild); };

  function skeleton(kind) {
    clear();
    const box = el('div');
    if (kind === 'index') {
      box.className = 'hlp__grid';
      for (let i = 0; i < 6; i++) box.appendChild(el('div', 'hlp__skel hlp__skel--card'));
    } else {
      for (let i = 0; i < 5; i++) box.appendChild(el('div', 'hlp__skel hlp__skel--row'));
    }
    view.appendChild(box);
  }

  /* The one state this page has that others do not: the answers are on another
     origin, so "we could not reach them" is a real and ordinary outcome. It
     names the server-rendered help centre, which is the same content one hop
     away, rather than apologising and stopping. */
  function down() {
    clear();
    const box = el('div', 'hlp__down');
    box.appendChild(el('p', 'hlp__title', 'We cannot reach the help centre just now.'));
    const p = el('p', null, 'It is usually a moment. You can also read it at ');
    const a = el('a', null, 'jennie.juce.co.za/help');
    a.href = api + '/help';
    a.rel = 'noopener';
    p.appendChild(a);
    p.appendChild(document.createTextNode('.'));
    box.appendChild(p);
    view.appendChild(box);
  }

  function crumb(items) {
    const nav = el('nav', 'hlp__crumb');
    nav.setAttribute('aria-label', 'Breadcrumb');
    items.forEach(([label, href], i) => {
      if (i) {
        const sep = el('span', null, '/');
        sep.setAttribute('aria-hidden', 'true');
        nav.appendChild(sep);
      }
      if (href) {
        const a = el('a', null, label);
        a.href = href;
        a.setAttribute('data-help-link', '');
        nav.appendChild(a);
      } else {
        const s = el('span', null, label);
        s.setAttribute('aria-current', 'page');
        nav.appendChild(s);
      }
    });
    return nav;
  }

  function rowList(items, empty) {
    if (!items.length) return el('p', 'hlp__empty', empty);
    const ul = el('ul', 'hlp__rows');
    items.forEach((it) => {
      const li = el('li');
      const a = el('a');
      a.href = hrefFor('article', it.slug);
      a.setAttribute('data-help-link', '');
      a.appendChild(el('b', null, it.title));
      if (it.summary) a.appendChild(el('i', null, it.summary));
      li.appendChild(a);
      ul.appendChild(li);
    });
    return ul;
  }

  /* ------------------------------------------------------------ views ---- */

  function renderIndex(d) {
    clear();
    setHero(true);
    head({
      title: `Help centre - ${shopName}`,
      description: 'Answers about JUCE orders, delivery, returns, rewards, gift cards and the fragrances themselves.',
      canonical: d.home,
      jsonLd: [{
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: d.categories.map((c, i) => ({
          '@type': 'ListItem', position: i + 1, name: c.title, url: c.url,
        })),
      }],
    });

    const grid = el('div', 'hlp__grid');
    d.categories.forEach((c) => {
      const a = el('a', 'hlp__card');
      a.href = hrefFor('category', c.slug);
      a.setAttribute('data-help-link', '');
      a.appendChild(icon(c.icon));
      a.appendChild(el('h2', null, c.title));
      if (c.blurb) a.appendChild(el('p', null, c.blurb));
      a.appendChild(el('span', 'hlp__count', `${c.count} article${c.count === 1 ? '' : 's'}`));
      grid.appendChild(a);
    });
    view.appendChild(grid);

    if (d.popular && d.popular.length) {
      const sect = el('section', 'hlp__sect');
      sect.appendChild(el('span', 'hlp__eyebrow', 'Most read'));
      sect.appendChild(rowList(d.popular, ''));
      view.appendChild(sect);
    }
  }

  function renderCategory(c) {
    clear();
    setHero(false);
    head({
      title: `${c.title} - help centre - ${shopName}`,
      description: c.seoDescription || c.blurb || `${c.title} answers from the JUCE help centre.`,
      canonical: c.url,
      jsonLd: [{
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: c.title,
        itemListElement: c.articles.map((a, i) => ({
          '@type': 'ListItem', position: i + 1, name: a.title, url: a.url,
        })),
      }],
    });

    view.appendChild(crumb([['Help centre', hrefFor('home')], [c.title, null]]));
    const head2 = el('header', 'hlp__head');
    head2.appendChild(el('h1', 'hlp__title', c.title));
    if (c.blurb) head2.appendChild(el('p', 'hlp__lede', c.blurb));
    view.appendChild(head2);
    view.appendChild(rowList(c.articles, 'Nothing in here yet.'));
  }

  function renderArticle(a) {
    clear();
    setHero(false);
    head({
      title: `${a.title} - ${shopName}`,
      description: a.seoDescription || a.summary || '',
      canonical: a.url,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [{
            '@type': 'Question',
            name: a.title,
            acceptedAnswer: { '@type': 'Answer', text: a.text || a.summary || '' },
          }],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Help centre', item: (index && index.home) || a.url },
          ].concat(a.category ? [{ '@type': 'ListItem', position: 2, name: a.category.title, item: a.category.url }] : [])
            .concat([{ '@type': 'ListItem', position: a.category ? 3 : 2, name: a.title, item: a.url }]),
        },
      ],
    });

    view.appendChild(crumb(
      [['Help centre', hrefFor('home')]]
        .concat(a.category ? [[a.category.title, hrefFor('category', a.category.slug)]] : [])
        .concat([[a.title, null]])
    ));

    const doc = el('article', 'hlp__doc');
    doc.appendChild(el('h1', null, a.title));
    if (a.summary) doc.appendChild(el('p', 'hlp__answer', a.summary));

    /* THE ONE innerHTML ON THIS PAGE, and the only field it is allowed on.
       `html` is what core/help.ts rendered from the article's markdown with a
       renderer that emits paragraphs, lists, links, emphasis and nothing else
       - no script, no iframe, no image, no attribute a caller chose. Every
       other string from this API is set with textContent. */
    const body = el('div', 'hlp__body rte');
    body.innerHTML = a.html || '';
    doc.appendChild(body);

    if (a.updatedAt) doc.appendChild(el('p', 'hlp__stamp', `Last updated ${String(a.updatedAt).slice(0, 10)}`));
    view.appendChild(doc);
    view.appendChild(voteBox(a.slug));

    if (a.related && a.related.length) {
      const sect = el('section', 'hlp__sect');
      sect.appendChild(el('span', 'hlp__eyebrow', 'Related'));
      sect.appendChild(rowList(a.related, ''));
      view.appendChild(sect);
    }
  }

  function renderResults(term, results) {
    clear();
    setHero(false);
    /* A results page must never be the canonical anything, and it is not a
       page anybody should land on from a search engine either. */
    head({
      title: `${term} - help centre - ${shopName}`,
      description: 'Search the JUCE help centre.',
      canonical: (index && index.home) || '',
      jsonLd: [],
    });
    view.appendChild(crumb([['Help centre', hrefFor('home')], ['Search', null]]));
    const h = el('header', 'hlp__head');
    h.appendChild(el('h1', 'hlp__title',
      results.length ? `${results.length} answer${results.length === 1 ? '' : 's'}` : 'Nothing for that'));
    h.appendChild(el('p', 'hlp__lede', `You searched for "${term}".`));
    view.appendChild(h);
    view.appendChild(rowList(results, 'Nothing matched. Try fewer words, or ask below.'));
  }

  /* --------------------------------------------------------- feedback ---- */

  function voteBox(slug) {
    const box = el('section', 'hlp__vote');
    box.appendChild(el('p', null, 'Did this answer it?'));
    const row = el('div', 'hlp__votes');
    const yes = el('button', null, 'Yes');
    const no = el('button', null, 'Not really');
    [yes, no].forEach((b) => { b.type = 'button'; b.setAttribute('aria-pressed', 'false'); });
    row.appendChild(yes); row.appendChild(no);
    box.appendChild(row);

    const note = el('textarea', 'hlp__note');
    note.maxLength = 1000;
    note.placeholder = 'What were you hoping it would say?';
    note.setAttribute('aria-label', 'What were you hoping it would say?');
    box.appendChild(note);
    const said = el('small', 'hlp__said');
    box.appendChild(said);

    let sent = false;
    const send = (helpful, comment) =>
      fetch(api + '/api/help/feedback', {
        method: 'POST', credentials: 'omit',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ slug, helpful, comment: comment || null }),
      }).catch(() => {});

    yes.addEventListener('click', () => {
      if (sent) return;
      sent = true;
      yes.setAttribute('aria-pressed', 'true');
      no.setAttribute('aria-pressed', 'false');
      box.setAttribute('data-said', 'up');
      send(true, null);
      said.textContent = 'Thank you. That tells us to leave it alone.';
    });
    no.addEventListener('click', () => {
      if (sent) return;
      no.setAttribute('aria-pressed', 'true');
      yes.setAttribute('aria-pressed', 'false');
      box.setAttribute('data-said', 'down');
      said.textContent = 'Tell us what was missing and we will write it up.';
      note.focus();
    });
    const finish = () => {
      if (sent || !note.value.trim()) return;
      sent = true;
      send(false, note.value.trim());
      note.disabled = true;
      said.textContent = 'Got it. Somebody reads every one of these.';
    };
    note.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); finish(); }
    });
    note.addEventListener('blur', finish);
    return box;
  }

  /* ----------------------------------------------------------- router ---- */

  /* ONE ENTRY POINT FOR EVERY VIEW CHANGE, so the head, the scroll position
     and the history entry can never get out of step with what is drawn. */
  function show(url, { push } = {}) {
    const q = new URL(url, location.origin).searchParams;
    const article = q.get('a');
    const category = q.get('c');
    const term = q.get('q');

    if (push) history.pushState({}, '', url);
    if (findInput && !term) findInput.value = '';
    closeSuggestions();

    if (article) {
      const cached = articles.get(article);
      if (cached) { renderArticle(cached); return; }
      skeleton('rows');
      const qs = lastQueryId ? `?qid=${lastQueryId}` : '';
      get(`/api/help/article/${encodeURIComponent(article)}${qs}`)
        .then((a) => { articles.set(article, a); renderArticle(a); })
        .catch((err) => (String(err.message) === '404' ? notFound() : down()));
      return;
    }
    if (category) {
      skeleton('rows');
      get(`/api/help/category/${encodeURIComponent(category)}`)
        .then(renderCategory)
        .catch((err) => (String(err.message) === '404' ? notFound() : down()));
      return;
    }
    if (term) {
      skeleton('rows');
      get(`/api/help/search?s=theme&q=${encodeURIComponent(term)}`)
        .then((d) => { lastQueryId = d.queryId; renderResults(term, d.results || []); })
        .catch(down);
      return;
    }
    skeleton('index');
    loadIndex().then(renderIndex).catch(down);
  }

  function notFound() {
    clear();
    setHero(false);
    const box = el('div', 'hlp__empty');
    box.appendChild(el('p', null, 'That answer is not here. It may have been rewritten under a different name.'));
    const a = el('a', null, 'Back to the help centre');
    a.href = hrefFor('home');
    a.setAttribute('data-help-link', '');
    box.appendChild(a);
    view.appendChild(box);
  }

  /* Every internal link is intercepted once, at the root, so a link rendered
     into any view works without each renderer wiring its own handler. */
  root.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('a[data-help-link]');
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    show(a.getAttribute('href'), { push: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
  window.addEventListener('popstate', () => show(location.href));

  /* ----------------------------------------------------------- search ---- */

  function closeSuggestions() {
    if (!sug) return;
    sug.setAttribute('data-open', '0');
    while (sug.firstChild) sug.removeChild(sug.firstChild);
  }

  if (findForm && findInput && sug) {
    let timer = null;
    let seq = 0;

    const draw = (d) => {
      while (sug.firstChild) sug.removeChild(sug.firstChild);
      lastQueryId = d.queryId;
      const list = (d.results || []).slice(0, 6);
      if (!list.length) {
        sug.appendChild(el('p', null, 'Nothing for that yet. Press enter to ask us.'));
        sug.setAttribute('data-open', '1');
        return;
      }
      list.forEach((r) => {
        const a = el('a');
        a.href = hrefFor('article', r.slug);
        a.setAttribute('data-help-link', '');
        a.setAttribute('role', 'option');
        a.appendChild(el('b', null, r.title));
        if (r.summary) a.appendChild(el('i', null, r.summary));
        sug.appendChild(a);
      });
      sug.setAttribute('data-open', '1');
    };

    const ask = () => {
      const term = findInput.value.trim();
      if (term.length < 2) { closeSuggestions(); return; }
      const mine = ++seq;
      get(`/api/help/search?s=theme&q=${encodeURIComponent(term)}`)
        .then((d) => { if (mine === seq) draw(d); })
        .catch(() => closeSuggestions());
    };

    findInput.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(ask, 180); });
    findInput.addEventListener('focus', () => { if (findInput.value.trim().length > 1) ask(); });
    findInput.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeSuggestions(); });
    document.addEventListener('click', (e) => { if (!findForm.contains(e.target)) closeSuggestions(); });

    findForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const term = findInput.value.trim();
      if (term.length < 2) return;
      show(`/pages/help?q=${encodeURIComponent(term)}`, { push: true });
      findInput.value = term;
    });
  }

  /* -------------------------------------------------------------- ask ---- */

  const askForm = root.querySelector('[data-help-ask]');
  const askOut = root.querySelector('[data-help-ask-out]');
  if (askForm && askOut) {
    askForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = askForm.querySelector('input[name=question]');
      const question = input.value.trim();
      if (question.length < 6) { askOut.textContent = 'A few more words and we can look.'; return; }
      askOut.textContent = 'Looking...';
      fetch(api + '/api/help/ask', {
        method: 'POST', credentials: 'omit',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ question }),
      }).then((r) => r.json()).then((d) => {
        while (askOut.firstChild) askOut.removeChild(askOut.firstChild);
        if (d && d.answered && d.results && d.results.length) {
          askOut.appendChild(el('p', null, 'This might be it:'));
          d.results.forEach((r) => {
            const a = el('a', null, r.title);
            a.href = hrefFor('article', r.slug);
            a.setAttribute('data-help-link', '');
            askOut.appendChild(a);
          });
        } else {
          askOut.textContent = 'Noted, thank you. Nobody has written that one up yet, and now it is on the list.';
          input.value = '';
        }
      }).catch(() => {
        askOut.textContent = 'That did not send. The chat is the fastest way through.';
      });
    });
  }

  /* The first paint. */
  show(location.href);
}

/* Mounted from snippets/help-centre.liquid, which is the only page that loads
   this file - so it self-starts rather than waiting for theme.js to know about
   it. The `mounted` guard above makes a second call a no-op. */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHelp, { once: true });
} else {
  initHelp();
}
